import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api-client';

interface DebugLog {
  time: string;
  type: 'info' | 'success' | 'error' | 'warning';
  message: string;
}

export default function ExpoTokenDebugPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [backendTokens, setBackendTokens] = useState<any>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  const addLog = (type: DebugLog['type'], message: string) => {
    const log: DebugLog = {
      time: new Date().toLocaleTimeString(),
      type,
      message
    };
    setLogs(prev => [...prev, log]);
  };

  const checkEnvironment = () => {
    addLog('info', '🔍 Checking environment...');
    addLog('info', `- Is Native App: ${window.isNativeApp ? 'YES ✅' : 'NO ❌'}`);
    addLog('info', `- User Logged In: ${user ? 'YES ✅' : 'NO ❌'}`);
    if (user) {
      addLog('info', `- User: ${user.firstName} ${user.lastName}`);
      addLog('info', `- User ID: ${user._id}`);
    }
    addLog('info', `- Window Token: ${window.expoPushToken ? 'EXISTS ✅' : 'NOT SET ❌'}`);
    if (window.expoPushToken) {
      addLog('info', `- Token: ${window.expoPushToken.substring(0, 30)}...`);
    }
    addLog('info', `- Platform: ${window.devicePlatform || 'UNKNOWN'}`);
    
    const storedToken = localStorage.getItem('pending_expo_push_token');
    addLog('info', `- Stored Token: ${storedToken ? 'EXISTS ✅' : 'NOT STORED ❌'}`);
    if (storedToken) {
      addLog('info', `- Stored Token: ${storedToken.substring(0, 30)}...`);
    }
  };

  const requestToken = () => {
    addLog('info', '📱 Requesting push token from native app...');
    if (window.isNativeApp && window.requestPushToken) {
      window.requestPushToken();
      addLog('success', '✅ Token request sent');
    } else {
      addLog('error', '❌ Not a native app or requestPushToken not available');
    }
  };

  const manualRegister = async () => {
    if (!user) {
      addLog('error', '❌ Please login first');
      return;
    }

    const token = window.expoPushToken || localStorage.getItem('pending_expo_push_token');
    if (!token) {
      addLog('error', '❌ No token available. Request token first.');
      return;
    }

    setIsRegistering(true);
    addLog('info', '📤 Manually registering token with backend...');

    try {
      const response = await api.post('/expo/update-push-token', {
        expoPushToken: token,
        platform: window.devicePlatform || localStorage.getItem('pending_expo_platform') || 'unknown',
        device: navigator.userAgent
      });

      addLog('success', '✅ Token registered successfully!');
      addLog('info', `- Response: ${JSON.stringify(response.data)}`);
      fetchBackendTokens();
    } catch (error: any) {
      addLog('error', `❌ Registration failed: ${error.message}`);
      addLog('error', `- Status: ${error.response?.status}`);
      addLog('error', `- Response: ${JSON.stringify(error.response?.data)}`);
    } finally {
      setIsRegistering(false);
    }
  };

  const fetchBackendTokens = async () => {
    if (!user) {
      addLog('warning', '⚠️ Login required to fetch backend tokens');
      return;
    }

    addLog('info', '🔍 Fetching tokens from backend...');
    try {
      const response = await api.get('/expo/my-tokens');
      setBackendTokens(response.data);
      addLog('success', `✅ Found ${response.data.count} tokens in backend`);
      if (response.data.count === 0) {
        addLog('warning', '⚠️ No tokens registered in backend yet');
      }
    } catch (error: any) {
      addLog('error', `❌ Failed to fetch tokens: ${error.message}`);
    }
  };

  const sendTestNotification = async () => {
    if (!user) {
      addLog('error', '❌ Please login first');
      return;
    }

    addLog('info', '📮 Sending test notification...');
    try {
      const response = await api.post('/expo/test-notification');
      addLog('success', '✅ Test notification sent!');
      addLog('info', `- Results: ${JSON.stringify(response.data)}`);
    } catch (error: any) {
      addLog('error', `❌ Failed to send test: ${error.message}`);
      if (error.response?.data?.message) {
        addLog('error', `- Message: ${error.response.data.message}`);
      }
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  useEffect(() => {
    addLog('info', '🚀 Expo Token Debug Page loaded');
    checkEnvironment();

    // Listen for token events
    const handleTokenReceived = (event: CustomEvent) => {
      addLog('success', '🎉 Token event received!');
      addLog('info', `- Token: ${event.detail.token?.substring(0, 30)}...`);
      addLog('info', `- Platform: ${event.detail.platform}`);
      checkEnvironment();
    };

    window.addEventListener('expoPushTokenReceived', handleTokenReceived as EventListener);

    if (user) {
      fetchBackendTokens();
    }

    return () => {
      window.removeEventListener('expoPushTokenReceived', handleTokenReceived as EventListener);
    };
  }, [user]);

  const getLogColor = (type: DebugLog['type']) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      default: return 'text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <h1 className="text-2xl mb-4">🔧 Expo Token Debug</h1>
          
          {/* Status Cards */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded">
              <div className="text-sm text-gray-600">Native App</div>
              <div className="text-xl">{window.isNativeApp ? '✅ YES' : '❌ NO'}</div>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <div className="text-sm text-gray-600">User Status</div>
              <div className="text-xl">{user ? '✅ Logged In' : '❌ Not Logged In'}</div>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <div className="text-sm text-gray-600">Token in Window</div>
              <div className="text-xl">{window.expoPushToken ? '✅ YES' : '❌ NO'}</div>
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <div className="text-sm text-gray-600">Backend Tokens</div>
              <div className="text-xl">{backendTokens ? `${backendTokens.count} tokens` : 'Loading...'}</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={checkEnvironment}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              🔍 Check Environment
            </button>
            <button
              onClick={requestToken}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
              disabled={!window.isNativeApp}
            >
              📱 Request Token
            </button>
            <button
              onClick={manualRegister}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              disabled={!user || isRegistering}
            >
              {isRegistering ? '⏳ Registering...' : '📤 Register Token'}
            </button>
            <button
              onClick={fetchBackendTokens}
              className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
              disabled={!user}
            >
              🔄 Refresh Backend
            </button>
            <button
              onClick={sendTestNotification}
              className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
              disabled={!user}
            >
              📮 Send Test
            </button>
            <button
              onClick={clearLogs}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              🗑️ Clear Logs
            </button>
          </div>

          {/* Backend Tokens */}
          {backendTokens && backendTokens.count > 0 && (
            <div className="mb-6 p-4 bg-green-50 rounded">
              <h3 className="text-lg mb-2">Backend Tokens ({backendTokens.count})</h3>
              {backendTokens.tokens.map((token: any, idx: number) => (
                <div key={idx} className="mb-2 p-2 bg-white rounded text-sm">
                  <div><strong>Platform:</strong> {token.platform}</div>
                  <div><strong>Token:</strong> {token.token.substring(0, 40)}...</div>
                  <div><strong>Added:</strong> {new Date(token.addedAt).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}

          {/* Logs */}
          <div className="bg-gray-900 text-gray-100 p-4 rounded h-96 overflow-y-auto font-mono text-sm">
            {logs.length === 0 && (
              <div className="text-gray-500">No logs yet. Click buttons above to start debugging.</div>
            )}
            {logs.map((log, idx) => (
              <div key={idx} className="mb-1">
                <span className="text-gray-500">[{log.time}]</span>{' '}
                <span className={getLogColor(log.type)}>{log.message}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <h3 className="text-lg mb-2">📋 Instructions</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Make sure you're logged in</li>
            <li>Click "Check Environment" to see current status</li>
            <li>Click "Request Token" to get token from native app</li>
            <li>Click "Register Token" to send token to backend</li>
            <li>Click "Refresh Backend" to verify token is saved</li>
            <li>Click "Send Test" to receive a test notification</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
